import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Button from "react-bootstrap/Button";
import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { ORGANIZATIONS_URL } from "@constants/routes";
import { ModalTypes } from "@enums/modal-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import { OrganizationConfig } from "@idw-types/data/organization-config";
import { AxnIdentityProvider } from "@idw-types/data/reference-data/axn";
import { AxnAdminUser } from "@idw-types/data/axn-admin-user";
import { IdentityProviderDetailsReferenceData } from "@idw-types/state/identity-provider-details-state";
import { CustomerSupportTier } from "@idw-types/data/reference-data/generic/customer-support-tier";
import { RpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { updateFullOrganizationDetailsRequestByStep } from "@requests/organization-details/update-full-organization-details";
import { setOrganizationDetails } from "@redux/actions/organization-details-actions";
import { updateOrganizationDetailsReduxRequest } from "@redux-requests/organization-details";
import { getVerificationServiceDetailsAxnRequest } from "@requests/service-configuration";
import { navigateToUtil } from "@utils/navigation-util";
import { getOrganizationDetailsValidationErrors } from "@utils/validation/organization/organization-details/organization-details-validation-util";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { OrganizationDetailsAdminLoginSettingsTab } from "@containers/organization/organization-details/organization-details-component/organization-details-admin-login-settings-tab/organization-details-admin-login-settings-tab";
import { OrganizationDetailsGeneralTab } from "@containers/organization/organization-details/organization-details-component/organization-details-general-tab/organization-details-general-tab";
import { UpdateOrganizationUsersModal } from "@containers/modals/update-organization-users-modal/update-organization-users-modal";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import styles from "./organization-details-component.less";

type OrganizationDetailsComponentProps = {
    organizationConfig: OrganizationConfig;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    identityProviderReferenceData: IdentityProviderDetailsReferenceData;
    organizationUsers: Array<AxnAdminUser>;
    customerSupportTiers: Array<CustomerSupportTier>;
    canCreateIdentityProviders: boolean;
    onNewIdpCreated: (identityProvider: AxnIdentityProvider) => void;
    setAxnAdminUsers: (organizationUsers: Array<AxnAdminUser>) => void;
};

export const OrganizationDetailsComponent = (props: OrganizationDetailsComponentProps) => {
    const dispatch = useDispatch();
    const modalContext = useModalContext();
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const {
        organizationConfig,
        axnIdentityProviders,
        identityProviderReferenceData,
        organizationUsers,
        customerSupportTiers,
        canCreateIdentityProviders,
        onNewIdpCreated,
        setAxnAdminUsers
    } = props;

    const [requestInProgress, setRequestInProgress] = useState(false);
    const [organizationConfigState, setOrganizationConfigState] = useState<OrganizationConfig>(organizationConfig);
    const [updateOrganizationDetailStep, setUpdateOrganizationDetailStep] = useState(null);
    const [isLoadingVerificationService, setIsLoadingVerificationService] = useState(false);
    const [unformattedVerificationServiceDetails, setUnformattedVerificationServiceDetails] = useState<RpServiceConfiguration>(null);

    const getUnformattedVerificationServiceDetails = async (serviceID: string) => {
        setIsLoadingVerificationService(true);
        const serviceDetails = await getVerificationServiceDetailsAxnRequest(serviceID);
        if (serviceDetails.data) {
            setUnformattedVerificationServiceDetails(serviceDetails.data);
        }
        setIsLoadingVerificationService(false);
    };

    useEffect(() => {
        if (
            organizationConfig.loginServiceID &&
            organizationConfig.loginServiceIDPID &&
            !isLoadingVerificationService
        ) {
            //org config loaded and has a login service id - fetch the service
            getUnformattedVerificationServiceDetails(organizationConfig.loginServiceID);
        }
    }, [organizationConfig.loginServiceID, organizationConfig.loginServiceIDPID]);

    useEffect(() => {
        setOrganizationConfigState(cloneDeep(organizationConfig));
    }, [organizationConfig]);

    const stateIsUnchanged = useMemo(() => {
        return isEqual(organizationConfig, organizationConfigState);
    }, [organizationConfig, organizationConfigState]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-organization-details-changes",
        modalType: ModalTypes.DiscardOrganizationDetailsChanges,
        stateIsUnchanged
    });

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);

    const onCloseClick = () => {
        navigateToUtil(ORGANIZATIONS_URL, navigate, routerLocation.pathname);
    };

    const cancelUpdateOrganizationDetailsWithCorporateLogin = () => {
        setOrganizationConfigState(cloneDeep(organizationConfig));
    };

    const handleShowOrganizationUsersModal = () => {
        const renderModal = (onModalClose: () => void) => {

            return (
                <UpdateOrganizationUsersModal
                    organizationUsers={organizationUsers}
                    cancelUpdateOrganizationDetailsWithCorporateLogin={cancelUpdateOrganizationDetailsWithCorporateLogin}
                    performUpdateOrganizationDetailsWithCorporateLogin={performUpdateOrganizationDetailsWithCorporateLogin}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.UpdateAxnAdminUsersCredentials,
            renderFunction: renderModal
        });
    };

    const performUpdateOrganizationDetailsWithCorporateLogin = async (axnAdminUsers: Array<AxnAdminUser>) => {
        const hasIDPChanged = organizationConfig.loginServiceIDPID !== organizationConfigState.loginServiceIDPID;

        const result = await updateFullOrganizationDetailsRequestByStep(dispatch)(
            updateOrganizationDetailStep,
            organizationConfigState,
            organizationConfig,
            unformattedVerificationServiceDetails,
            hasIDPChanged,
            axnAdminUsers
        );

        if (result) {
            if (result.failedStep) {
                setUpdateOrganizationDetailStep(result.failedStep);
            } else {
                setUpdateOrganizationDetailStep(null);
                dispatch(setOrganizationDetails(organizationConfigState));
                if (result.verificationServiceDetails) {
                    setUnformattedVerificationServiceDetails(result.verificationServiceDetails);
                }
                if (result.axnAdminUsersUpdated) {
                    setAxnAdminUsers(result.axnAdminUsersUpdated);
                }
            }
        }

        return result;
    };

    const handleSubmit = async () => {
        if (
            organizationConfigState.loginServiceID &&
            organizationConfig.loginServiceIDPID !== organizationConfigState.loginServiceIDPID &&
            organizationUsers.length > 0
        ) {
            handleShowOrganizationUsersModal();
        } else if (organizationConfigState.loginServiceID) {
            setRequestInProgress(true);
            await performUpdateOrganizationDetailsWithCorporateLogin(organizationUsers);
            setRequestInProgress(false);
        } else {
            setRequestInProgress(true);
            await updateOrganizationDetailsReduxRequest(dispatch)(organizationConfigState);
            setRequestInProgress(false);
        }
    };

    const organizationDetailsValidationErrors = useMemo(() => {
        return getOrganizationDetailsValidationErrors(organizationConfigState);
    }, [organizationConfigState]);

    const shouldShowIDPIDWarning = useMemo(() => {
        return (
            organizationConfig.loginServiceIDPID !== organizationConfigState.loginServiceIDPID &&
            organizationUsers.length > 0
        );
    }, [
        organizationConfig.loginServiceIDPID,
        organizationConfigState.loginServiceIDPID,
        organizationUsers
    ]);

    return (
        <div className={styles.organizationDetailsComponent}>

            <div className={styles.headerContainer}>
                <div className={styles.organizationName}>
                    {`${organizationConfigState.name}`}
                </div>

                <div className={styles.buttonContainer}>
                    <Button
                        id="organization-details-close-button"
                        onClick={onCloseClick}
                        variant={"outline-secondary"}
                    >
                        {resources.common.buttons.close.label}
                    </Button>
                    {organizationConfig.permissions.canModify && (
                        <ButtonWithSpinner
                            id="organization-details-submit-button"
                            disabled={requestInProgress || stateIsUnchanged || organizationDetailsValidationErrors.count > 0}
                            hideSpinner={stateIsUnchanged || organizationDetailsValidationErrors.count > 0}
                            variant="primary"
                            type={ButtonTypes.BUTTON}
                            onClick={handleSubmit}
                            label={resources.common.buttons.save.label}
                        />
                    )}
                </div>
            </div>
            <TabContainer>
                {/* @ts-ignore */}
                <Tabs id="organization-details" fill={true} unmountOnExit={true}>
                    <Tab eventKey={"organization-details-main"} title={organizationDetailsValidationErrors.details.tabTitle}>
                        <OrganizationDetailsGeneralTab
                            isDisabled={!organizationConfig.permissions.canModify || requestInProgress}
                            canModifyCustomerSupportTier={organizationConfig.permissions.canModifyCustomerSupportTier}
                            organizationConfigState={organizationConfigState}
                            customerSupportTiers={customerSupportTiers}
                            labelWidth={labelWidth}
                            validationErrors={organizationDetailsValidationErrors.details}
                            setOrganizationConfigState={setOrganizationConfigState} />
                    </Tab>
                    {organizationConfig.permissions.canViewLoginSettings && (
                        <Tab eventKey={"organization-details-admin-login-access"} title={resources.pages.organizationDetails.tabs.adminLoginSettings.tabName}>
                            {isLoadingVerificationService && (
                                <LoadingIndicator title={resources.common.loadingIndicatorText} />
                            )}
                            {!isLoadingVerificationService && (
                                <OrganizationDetailsAdminLoginSettingsTab
                                    isDisabled={!organizationConfig.permissions.canModify || requestInProgress || !organizationConfig.permissions.canModifyLoginSettings}
                                    canCreateIdentityProviders={canCreateIdentityProviders}
                                    organizationConfigState={organizationConfigState}
                                    axnIdentityProviders={axnIdentityProviders}
                                    serviceApiKey={unformattedVerificationServiceDetails?.apiKey}
                                    serviceName={unformattedVerificationServiceDetails?.serviceName}
                                    identityProviderReferenceData={identityProviderReferenceData}
                                    shouldShowIDPIDWarning={shouldShowIDPIDWarning}
                                    setOrganizationConfigState={setOrganizationConfigState}
                                    onNewIdpCreated={onNewIdpCreated}
                                />
                            )}
                        </Tab>
                    )}
                </Tabs>
            </TabContainer>
        </div >
    );
};